import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaSave, FaPlus, FaTrash, FaGraduationCap, FaBriefcase, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './EditProfileScreen.css';

const EditProfileScreen = () => {
  const { user, setUser } = useUser();
  const [about, setAbout] = useState(user?.about || '');
  const [educationList, setEducationList] = useState(user?.education || []);
  const [experienceList, setExperienceList] = useState(user?.experience || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState({
    degree: '',
    institution: '',
    year: '',
    role: '',
    organization: '',
    certificate: null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const openModal = (type) => {
    setModalType(type);
    if (type === 'education') {
      setModalData({
        degree: '',
        institution: '',
        year: '',
        certificate: null,
      });
    } else if (type === 'experience') {
      setModalData({
        role: '',
        organization: '',
        year: '',
        certificate: null,
      });
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const handleAdd = async () => {
    const accessToken = localStorage.getItem('ACCESS_TOKEN');
    
    if (modalType === 'education') {
      if (!modalData.degree || !modalData.institution || !modalData.year) {
        toast.error('Please fill out all fields for education.');
        return;
      }
    } else if (modalType === 'experience') {
      if (!modalData.role || !modalData.organization || !modalData.year) {
        toast.error('Please fill out all fields for experience.');
        return;
      }
    }

    let certificateData = modalData.certificate;
    if (certificateData && !certificateData.public_id && certificateData instanceof File) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('selectedFile', certificateData);
        const response = await fetch(`${API_BASE_URL}api/upload/certificateFile`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload certificate');
        }
        const data = await response.json();
        if (data && data.public_id && data.secure_url) {
          certificateData = {
            public_id: data.public_id,
            secure_url: data.secure_url,
          };
        } else {
          throw new Error('Invalid upload response');
        }
      } catch (error) {
        setIsUploading(false);
        toast.error(error.message || 'Failed to upload certificate.');
        return;
      }
      setIsUploading(false);
    }

    const entry = { ...modalData, certificate: certificateData };
    if (modalType === 'education') {
      setEducationList([...educationList, entry]);
    } else if (modalType === 'experience') {
      setExperienceList([...experienceList, entry]);
    }
    closeModal();
  };

  const handleCertificateUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setModalData((prevData) => ({
        ...prevData,
        certificate: file,
      }));
    }
  };

  const deleteEducation = (index) => {
    setEducationList(educationList.filter((_, i) => i !== index));
  };

  const deleteExperience = (index) => {
    setExperienceList(experienceList.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!about || educationList.length === 0 || experienceList.length === 0) {
      toast.error('Please fill out all sections before saving.');
      return;
    }

    if (window.confirm('Are you sure you want to save your profile details?')) {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem('ACCESS_TOKEN');
        
        const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            about,
            education: educationList,
            experience: experienceList,
          }),
        });

        if (response.ok) {
          setUser({
            ...user,
            about,
            education: educationList,
            experience: experienceList,
          });
          toast.success('Profile updated successfully!');
        } else {
          throw new Error('Failed to save profile');
        }
      } catch (error) {
        console.error('Error saving profile:', error);
        toast.error('Failed to update profile. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const renderCertificate = (certificate) => {
    if (!certificate) return null;
    
    if (certificate.secure_url) {
      return (
        <div className="certificate-preview" onClick={() => window.open(certificate.secure_url, '_blank')}>
          <FaFileAlt className="certificate-icon" />
          <span className="certificate-text">View Certificate</span>
        </div>
      );
    }
    
    return (
      <div className="certificate-preview">
        <FaFileAlt className="certificate-icon" />
        <span className="certificate-text">{certificate.name || 'Certificate'}</span>
      </div>
    );
  };

  return (
    <DashboardLayout title="Edit Profile" showBackButton={true}>
      <div className="edit-profile-container">
        <div className="profile-header">
          <h1>Edit About, Education, and Experience</h1>
          <p>Update your About, Education, and Experience details to make your profile more complete.</p>
        </div>

        <div className="profile-form">
          {/* About Section */}
          <div className="form-section">
            <h3>About</h3>
            <textarea
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="Tell patients about yourself, your expertise, and approach to healthcare..."
              className="about-textarea"
              rows="4"
            />
          </div>

          {/* Education Section */}
          <div className="form-section">
            <div className="section-header">
              <h3><FaGraduationCap /> Education</h3>
              <button 
                type="button" 
                className="add-button"
                onClick={() => openModal('education')}
              >
                <FaPlus /> Add Education
              </button>
            </div>
            
            {educationList.length === 0 ? (
              <div className="empty-state">
                <FaGraduationCap className="empty-icon" />
                <p>Add your educational qualifications</p>
              </div>
            ) : (
              <div className="items-list">
                {educationList.map((education, index) => (
                  <div key={index} className="item-card">
                    <div className="item-content">
                      <h4>{education.degree}</h4>
                      <p>{education.institution}</p>
                      <span className="year">{education.year}</span>
                      {renderCertificate(education.certificate)}
                    </div>
                    <button 
                      className="delete-button"
                      onClick={() => deleteEducation(index)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Experience Section */}
          <div className="form-section">
            <div className="section-header">
              <h3><FaBriefcase /> Experience</h3>
              <button 
                type="button" 
                className="add-button"
                onClick={() => openModal('experience')}
              >
                <FaPlus /> Add Experience
              </button>
            </div>
            
            {experienceList.length === 0 ? (
              <div className="empty-state">
                <FaBriefcase className="empty-icon" />
                <p>Add your work experience</p>
              </div>
            ) : (
              <div className="items-list">
                {experienceList.map((experience, index) => (
                  <div key={index} className="item-card">
                    <div className="item-content">
                      <h4>{experience.role}</h4>
                      <p>{experience.organization}</p>
                      <span className="year">{experience.year}</span>
                      {renderCertificate(experience.certificate)}
                    </div>
                    <button 
                      className="delete-button"
                      onClick={() => deleteExperience(index)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button 
            className="save-button"
            onClick={handleSave}
            disabled={loading}
          >
            <FaSave />
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        {/* Modal */}
        {modalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              {isUploading && (
                <div className="upload-overlay">
                  <div className="upload-spinner">
                    <div className="spinner"></div>
                    <p>Uploading...</p>
                  </div>
                </div>
              )}
              
              <div className="modal-header">
                <h3>{modalType === 'education' ? 'Add Education' : 'Add Experience'}</h3>
                <button className="close-button" onClick={closeModal}>×</button>
              </div>
              
              {modalType === 'education' ? (
                <>
                  <input
                    type="text"
                    placeholder="Degree (e.g., MBBS, MD)"
                    value={modalData.degree}
                    onChange={(e) => setModalData({...modalData, degree: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Institution (e.g., XYZ University)"
                    value={modalData.institution}
                    onChange={(e) => setModalData({...modalData, institution: e.target.value})}
                  />
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Role (e.g., General Practitioner)"
                    value={modalData.role}
                    onChange={(e) => setModalData({...modalData, role: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Organization (e.g., XYZ Hospital)"
                    value={modalData.organization}
                    onChange={(e) => setModalData({...modalData, organization: e.target.value})}
                  />
                </>
              )}
              
              <input
                type="text"
                placeholder="Year (e.g., 2020)"
                value={modalData.year}
                onChange={(e) => setModalData({...modalData, year: e.target.value})}
              />
              
              <div className="certificate-upload">
                <label>Upload Certificate</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleCertificateUpload}
                  className="file-input"
                />
                {modalData.certificate && (
                  <div className="selected-file">
                    <FaFileAlt />
                    <span>{modalData.certificate.name || 'Certificate Selected'}</span>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button onClick={closeModal}>Cancel</button>
                <button onClick={handleAdd} className="primary" disabled={isUploading}>
                  {isUploading ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditProfileScreen;